import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { from, timer } from 'rxjs';
import {
  ConnectorApiService,
  ConnectorOAuthService,
} from '@brighthub/copilot';

type CallbackStatus = 'loading' | 'success' | 'error';

@Component({
  selector: 'an-oauth-callback',
  standalone: true,
  template: `
    <main class="flex min-h-screen items-center justify-center bg-surface-100 p-6">
      <section class="w-full max-w-md rounded-lg bg-surface-0 p-8 text-center shadow-lg">
        @if (status() === 'loading') {
          <h1 class="text-lg font-semibold text-surface-900">Finishing connection</h1>
          <p class="mt-2 text-sm text-surface-600">Please keep this page open.</p>
        } @else if (status() === 'success') {
          <h1 class="text-lg font-semibold text-green-700">Connection complete</h1>
          <p class="mt-2 text-sm text-surface-600">Taking you back...</p>
        } @else {
          <h1 class="text-lg font-semibold text-red-700">Connection failed</h1>
          <p class="mt-2 text-sm text-surface-600">{{ errorMessage() }}</p>
          <button
            type="button"
            class="mt-6 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-surface-0 hover:bg-primary-700"
            (click)="returnToApp()"
          >
            Go back
          </button>
        }
      </section>
    </main>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AskNaviOAuthCallbackComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly connectorApi = inject(ConnectorApiService);
  private readonly oauth = inject(ConnectorOAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private completionStarted = false;
  private returnTo = '/ask-navi';

  readonly status = signal<CallbackStatus>('loading');
  readonly errorMessage = signal('Please return to Ask Navi and try connecting again.');

  ngOnInit(): void {
    if (this.completionStarted) return;
    this.completionStarted = true;
    const sessionId = this.route.snapshot.queryParamMap.get('session_id')?.trim();
    const { mcpServerId, returnTo } = this.oauth.readReturnContext();
    this.returnTo = returnTo;
    if (!sessionId) {
      this.fail(
        'The callback URL did not include an OAuth session ID.',
        mcpServerId
      );
      return;
    }
    if (!mcpServerId) {
      this.fail(
        'The callback is missing its connection context. Return to the chat and try connecting again.',
        mcpServerId
      );
      return;
    }

    from(this.connectorApi.completeConnector(sessionId, mcpServerId))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result.connected) {
            this.fail(
              'We could not finish the connection. Return to the chat and try again.',
              mcpServerId
            );
            return;
          }
          this.oauth.clearReturnContext();
          this.status.set('success');
          const popupMode = this.oauth.notifyOpenerConnected(mcpServerId);
          timer(popupMode ? 500 : 1500)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              if (popupMode) window.close();
              else {
                void this.router.navigateByUrl(this.returnTo, {
                  replaceUrl: true,
                });
              }
            });
        },
        error: () => {
          this.fail(
            'We could not finish the connection. Return to the chat and try again.',
            mcpServerId
          );
        },
      });
  }

  returnToApp(): void {
    void this.router.navigateByUrl(this.returnTo, { replaceUrl: true });
  }

  private fail(message: string, mcpServerId: string): void {
    this.status.set('error');
    this.errorMessage.set(message);
    if (this.oauth.notifyOpenerFailed(mcpServerId, message)) {
      this.oauth.clearReturnContext();
      window.close();
    }
  }
}
